import Icons from "@/src/components/Icons";
import { useNavigate } from "react-router-dom";
import ItemComments from "./ItemComments";
import { useEffect, useState, useRef } from "react";
import { commentApi } from "@/src/utils/api/comments";
import Button from "@/src/components/Button";

interface CommentItem {
  id: string;
  userName: string;
  star: number;
  date: string;
  enterpriseName: string;
  foodName: string;
  comment: string;
  reply?: string;
}

function Comments() {
  const [data, setData] = useState<CommentItem[]>([]);
  const [reply, setReply] = useState<string>("ALL");
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [reload, setReload] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageSize = 30;
  let history = useNavigate();

  const fetchComments = async (
    pageNumber: number,
    isNewFilter: boolean = false
  ) => {
    if (loading) return;
    setLoading(true);

    try {
      const response = await commentApi.list(reply, pageNumber, pageSize);

      if (response.data && response.data.length > 0) {
        if (isNewFilter) {
          setData(response.data);
        } else {
          setData((prevData) => [...prevData, ...response.data]);
        }

        if (response.data.length < pageSize) {
          setHasMore(false);
        }
      } else {
        if (isNewFilter) {
          setData([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchComments(1, true);
  }, [reply]);
  useEffect(() => {
    if (reload > 0) {
      setHasMore(true);
      fetchComments(page, true);
    }
  }, [reload]);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasMore || loading || !containerRef.current) return;

      const container = containerRef.current;
      const { bottom } = container.getBoundingClientRect();

      if (bottom <= window.innerHeight + 200) {
        setPage((prevPage) => prevPage + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loading]);

  useEffect(() => {
    if (page > 1) {
      fetchComments(page, false);
    }
  }, [page]);

  return (
    <div ref={containerRef} className="pb-10">
      <h4
        className="title text-xl font-extrabold flex items-center cursor-pointer"
        onClick={() => history("/profile")}
      >
        <Icons name="arrowBack" fill="none" size={40} />
        نظر کابران
      </h4>
      <div className="items-btn flex gap-2 mt-4">
        <Button
          size="xsmall"
          variant={reply === "ALL" ? "default" : "outline"}
          className="text-sm py-2"
          onClick={() => setReply("ALL")}
        >
          همه
        </Button>
        <Button
          size="xsmall"
          variant={reply === "NOT_REPLIED" ? "default" : "outline"}
          className="text-sm py-2"
          onClick={() => setReply("NOT_REPLIED")}
        >
          بی‌پاسخ
        </Button>
        <Button
          size="xsmall"
          variant={reply === "REPLIED" ? "default" : "outline"}
          className="text-sm py-2"
          onClick={() => setReply("REPLIED")}
        >
          پاسخ داده
        </Button>
      </div>
      <div className="items flex flex-col mt-4 gap-4">
        {data.map((item, index) => (
          <ItemComments
            key={item.id || index}
            data={item}
            setReload={setReload}
            reload={reload}
          />
        ))}
      </div>
      {loading && (
        <div className="flex justify-center my-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-300"></div>
        </div>
      )}
      {!hasMore && data.length > 0 && (
        <div className="text-center text-gray-200-mmpz mt-4">پایان نتایج</div>
      )}
      {!hasMore && data.length === 0 && (
        <div className="text-center text-gray-200-mmpz mt-4">نظری یافت نشد</div>
      )}
    </div>
  );
}

export default Comments;
